import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit, Power, Trash2 } from "lucide-react";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { COUNTRIES, flagFor } from "@/lib/countries";
import { DatePicker } from "@/components/ui/date-picker";

const EMPTY = { email: "", name: "", password: "", role: "employee", job_title: "", phone: "", country: "", start_date: "" };

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const reload = async () => { const r = await api.get("/users"); setUsers(r.data); };
  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      if (editing) {
        const payload = { ...form }; if (!payload.password) delete payload.password; delete payload.email;
        await api.patch(`/users/${editing}`, payload); toast.success("User updated");
      } else {
        if (!form.password) return toast.error("Password required");
        await api.post("/users", form); toast.success("User created");
      }
      setOpen(false); setForm(EMPTY); setEditing(null); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const startEdit = (u) => {
    setEditing(u.id);
    setForm({ ...EMPTY, ...u, password: "" });
    setOpen(true);
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "Access revoked" : "Access restored");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeUser = async (u) => {
    const ok = window.confirm(
      `Permanently delete ${u.name} (${u.email})?\n\n` +
      `This removes their login and unassigns them from all projects and documents.\n` +
      `Historical records (time entries, daily reports, leaves, calendar assignments) will be kept with their name preserved for audit purposes.\n\n` +
      `This cannot be undone.`
    );
    if (!ok) return;
    try {
      await api.delete(`/users/${u.id}?hard=true`);
      toast.success(`${u.name} deleted`);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-eyebrow">EMPLOYEES</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight mt-1">Crew Manifest</h1>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(EMPTY); setEditing(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-user-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">
              <Plus className="h-4 w-4 mr-2" /> New Employee
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0a0a0a] border-[#222] rounded-none text-white">
            <DialogHeader><DialogTitle className="font-heading uppercase">{editing ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <F label="EMAIL" value={form.email} set={(v) => setForm({ ...form, email: v })} disabled={!!editing} testid="user-email" />
              <F label="NAME" value={form.name} set={(v) => setForm({ ...form, name: v })} testid="user-name" />
              <F label={editing ? "NEW PASSWORD (optional)" : "PASSWORD"} type="password" value={form.password} set={(v) => setForm({ ...form, password: v })} testid="user-password" />
              <div>
                <Label className="label-eyebrow">ROLE</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none">
                    <SelectItem value="employee">Employee</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <F label="JOB TITLE" value={form.job_title} set={(v) => setForm({ ...form, job_title: v })} />
              <F label="PHONE" value={form.phone} set={(v) => setForm({ ...form, phone: v })} />
              <div>
                <Label className="label-eyebrow">LOCATION</Label>
                <Select value={form.country || "none"} onValueChange={(v) => setForm({ ...form, country: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="user-country" className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-[#222] rounded-none max-h-72">
                    <SelectItem value="none">— None —</SelectItem>
                    {COUNTRIES.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        <span className="font-mono flex items-center gap-2">{flagFor(c.code)}{c.code} — {c.name}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="label-eyebrow">START DATE</Label>
                <div className="mt-2"><DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} /></div>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:justify-between">
              <div>
                {editing && editing !== currentUser?.id && (
                  <Button
                    variant="ghost"
                    onClick={async () => {
                      const u = users.find((x) => x.id === editing);
                      if (!u) return;
                      setOpen(false);
                      await removeUser(u);
                    }}
                    data-testid="delete-user-from-edit"
                    className="rounded-none border border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 font-heading uppercase"
                  >
                    <Trash2 className="h-4 w-4 mr-2" /> Delete user
                  </Button>
                )}
              </div>
              <Button onClick={submit} data-testid="save-user-btn" className="rounded-none btn-brand border border-[#3b2bd0] font-heading uppercase">{editing ? "Update" : "Create"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="rounded-none bg-[#0a0a0a] border-[#1c1c1c] overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-[#666] label-eyebrow border-b border-[#1c1c1c]">
              <Th>NAME</Th><Th>EMAIL</Th><Th>ROLE</Th><Th>TITLE</Th><Th>LOCATION</Th><Th>PHONE</Th><Th>START</Th><Th>STATUS</Th><Th>ACTIONS</Th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-[#1c1c1c] hover:bg-[#101010]">
                <Td>
                  <span className="flex items-center gap-2">
                    <span className="h-7 w-7 grid place-items-center overflow-hidden border border-[#3b2bd0]" style={{ backgroundColor: "#100755" }}>
                      {u.avatar_filename
                        ? <img src={`${API}/users/${u.id}/avatar`} alt="" className="h-full w-full object-cover" />
                        : <span className="font-heading text-xs font-bold uppercase text-white">{u.name?.[0]}</span>}
                    </span>
                    {u.name}
                  </span>
                </Td>
                <Td mono>{u.email}</Td>
                <Td><span className="label-eyebrow">{u.role}</span></Td>
                <Td>{u.job_title || "—"}</Td>
                <Td>{u.country ? <span title={u.country}>{flagFor(u.country)} <span className="font-mono text-xs text-[#888] ml-1">{u.country}</span></span> : <span className="font-mono text-xs text-[#666]">—</span>}</Td>
                <Td mono>{u.phone || "—"}</Td>
                <Td mono>{u.start_date || "—"}</Td>
                <Td>
                  {u.active
                    ? <Badge className="rounded-none bg-emerald-500/10 text-emerald-400 border-emerald-500/30 font-mono text-xs">ACTIVE</Badge>
                    : <Badge variant="outline" className="rounded-none border-red-500/30 text-red-400 font-mono text-xs">INACTIVE</Badge>}
                </Td>
                <Td>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(u)} className="rounded-none h-7 px-2"><Edit className="h-3 w-3" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => toggleActive(u)} className="rounded-none h-7 px-2"><Power className="h-3 w-3" /></Button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function F({ label, value, set, type = "text", disabled, testid }) {
  return (
    <div>
      <Label className="label-eyebrow">{label}</Label>
      <Input type={type} value={value || ""} onChange={(e) => set(e.target.value)} disabled={disabled} data-testid={testid} className="bg-black border-[#222] rounded-none mt-2 h-10 font-mono" />
    </div>
  );
}
const Th = ({ children }) => <th className="px-4 py-3 text-left font-normal">{children}</th>;
const Td = ({ children, mono }) => <td className={`px-4 py-3 ${mono ? "font-mono tabular text-xs" : ""}`}>{children}</td>;
